/**
 * The Alert Timeline card's expanded "Cards" view: the open alerts as pictures on a rail,
 * newest first, with the interval between neighbours written on the rail.
 *
 * Uniform spacing with the time STATED rather than a rail scaled to real time — four alerts
 * inside a minute would otherwise stack on one spot, and "four inside a minute" is exactly what
 * the order is read for (the same argument LatestAlertsStrip makes for the full panel).
 *
 * The card is a single-row strip in this view, so the column width follows the strip's HEIGHT:
 * whatever is left under the header decides how tall a 16:9 frame can be, and the frame decides
 * how wide its column is (150–216 px).
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import type { HexAlert } from '../../../api/contracts';
import type { AlertPlace } from '../LatestAlertsStrip';
import { useAlertImage } from '../../alerts/useAlertImage';
import { TONE, tint, FOCUS, SUNK } from './BoardParts';
import { useElementSize } from './useElementSize';

export const severityColor = (s?: string) =>
  s === 'critical' ? TONE.crit : s === 'warning' ? TONE.warn : TONE.info;
export const severityLabel = (s?: string) =>
  s === 'critical' ? 'Critical' : s === 'warning' ? 'Warning' : 'Info';

/** "4h 12m" / "19m" / "40s" — the gap between two alerts, or an alert's age. */
export function spanLabel(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d}d ${h % 24}h` : `${d}d`;
}

/** HexAlert timestamps arrive as epoch seconds on some hosts and ms on others. */
export function alertSeconds(a: HexAlert): number | null {
  const t = Number(a.timestamp);
  if (!Number.isFinite(t) || t <= 0) return null;
  return t > 1e12 ? t / 1000 : t;
}

function clockParts(sec: number | null) {
  if (!sec) return { hm: '—', ap: '' };
  const d = new Date(sec * 1000);
  const parts = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }).split(' ');
  return { hm: parts[0], ap: (parts[1] || '').toUpperCase() };
}

function TimelineCard({
  alert,
  width,
  newest,
  place,
  loadPreview,
  onOpen,
}: {
  alert: HexAlert;
  width: number;
  newest: boolean;
  place?: AlertPlace;
  loadPreview?: (a: HexAlert) => Promise<string | null>;
  onOpen?: () => void;
}) {
  const img = useAlertImage(alert, (a) => a.snapshotUrl || null, loadPreview);
  const color = severityColor(alert.severity);
  const sec = alertSeconds(alert);
  const { hm, ap } = clockParts(sec);
  const ago = sec ? spanLabel(Date.now() / 1000 - sec) : '';
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!onOpen}
      className={`flex flex-col overflow-hidden rounded-xl border border-border bg-card text-left shadow-card transition-[transform,box-shadow] duration-150 enabled:hover:-translate-y-0.5 enabled:hover:shadow-[0_10px_24px_rgba(16,24,40,0.12)] ${FOCUS}`}
      style={{ width, borderTop: `3px solid ${color}` }}
    >
      <span ref={img.ref} className="relative block flex-none overflow-hidden bg-muted" style={{ aspectRatio: '16 / 9' }}>
        {img.src ? (
          <img src={img.src} alt="" loading="lazy" onError={img.markFailed} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <span className="absolute inset-0" style={{ background: 'linear-gradient(160deg, #5b6472, #1f242c)' }} aria-hidden />
        )}
        <span className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent" aria-hidden />
        <span
          className="absolute right-1.5 top-1.5 rounded-full bg-white px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide"
          style={{ color }}
        >
          {severityLabel(alert.severity)}
        </span>
        {newest && (
          <span className="absolute left-1.5 top-1.5 rounded px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide text-white" style={{ backgroundColor: TONE.crit }}>
            Newest
          </span>
        )}
        <span className="absolute bottom-1.5 left-1.5 right-1.5 flex items-center gap-1 truncate text-[10px] font-semibold uppercase tracking-wide text-white">
          <VideoCameraIcon className="h-3 w-3 flex-none" aria-hidden />
          <span className="truncate">{place?.name || 'Camera'}</span>
        </span>
      </span>
      <span className="block px-2.5 pb-2 pt-1.5">
        <span className="flex items-baseline gap-1">
          <span className="text-[16px] font-bold leading-none tabular-nums text-foreground">{hm}</span>
          <span className="text-[10px] font-semibold text-foreground">{ap}</span>
          <span className="ml-auto truncate text-[11px] text-muted-foreground">{ago && `${ago} ago`}</span>
        </span>
        <span className="mt-1 flex items-center gap-1.5">
          <span className="h-2 w-2 flex-none rounded-full" style={{ backgroundColor: color }} aria-hidden />
          <span className="truncate text-[12px] font-semibold text-foreground" title={alert.alertName}>
            {alert.alertName || 'Alert'}
          </span>
        </span>
        {place?.site && <span className="block truncate text-[11px] text-muted-foreground">{place.site}</span>}
      </span>
    </button>
  );
}

export default function AlertTimeline({
  alerts,
  places,
  loading,
  loadPreview,
  onOpenAlert,
  onLoadMore,
  loadingMore,
  toolbar,
}: {
  alerts: HexAlert[];
  places?: Record<string, AlertPlace>;
  loading?: boolean;
  loadPreview?: (a: HexAlert) => Promise<string | null>;
  onOpenAlert?: (a: HexAlert) => void;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  /** Filters for the row above the rail (the page's severity selector). */
  toolbar?: ReactNode;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const [sizeRef, { height }] = useElementSize<HTMLDivElement>();
  // The same search the List view's DetailsPanel has — alert, severity, description, camera, site.
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const shown = useMemo(() => (!needle ? alerts : alerts.filter((a) => {
    const place = places?.[a.cameraId];
    return [a.alertName, a.eventType, a.description, severityLabel(a.severity), place?.name, place?.site]
      .filter(Boolean).join(' ').toLowerCase().includes(needle);
  })), [alerts, places, needle]);
  // Rail (22) + text under the frame (~62) + paddings (~18) — the rest is the 16:9 frame.
  const width = Math.round(Math.max(150, Math.min(216, ((height || 200) - 102) * (16 / 9))));

  const scrollBy = (dir: number) =>
    scroller.current?.scrollBy({ left: dir * (width + 12) * 2, behavior: 'smooth' });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-none items-center gap-2 px-4 pb-1">
        <span className="flex-none rounded-full border border-border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          Newest first
        </span>
        {needle && (
          <span className="flex-none rounded-full bg-card px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
            {shown.length} of {alerts.length}
          </span>
        )}
        <span className="flex-1" />
        {toolbar}
        <label className="relative flex-none">
          <span className="sr-only">Search alerts</span>
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search alerts…"
            className={`h-[28px] w-[200px] max-w-[40vw] rounded-lg border border-border bg-card pl-8 pr-2 text-[12px] text-foreground placeholder:text-muted-foreground ${FOCUS}`}
          />
        </label>
        <span className="flex flex-none gap-1">
          {[-1, 1].map((d) => {
            const Icon = d < 0 ? ChevronLeftIcon : ChevronRightIcon;
            return (
              <button
                key={d}
                type="button"
                onClick={() => scrollBy(d)}
                aria-label={d < 0 ? 'Newer alerts' : 'Older alerts'}
                className={`flex h-[26px] w-[26px] items-center justify-center rounded-full border border-border bg-card text-muted-foreground hover:text-foreground ${FOCUS}`}
              >
                <Icon className="h-3.5 w-3.5" />
              </button>
            );
          })}
        </span>
      </div>
      <div ref={sizeRef} className="min-h-0 flex-1">
        <div ref={scroller} className="flex h-full snap-x items-start gap-3 overflow-x-auto overflow-y-hidden px-4 pb-2.5 pt-1">
          <div className="flex flex-none flex-col items-center pt-[3px]">
            <span className="rounded-full px-2 py-[1px] text-[10px] font-bold uppercase tracking-wide text-white" style={{ backgroundColor: TONE.crit }}>
              Now
            </span>
          </div>
          {loading && alerts.length === 0 &&
            [0, 1, 2, 3].map((i) => (
              <div key={i} className="flex-none animate-pulse rounded-xl bg-muted" style={{ width, height: width * 0.5625 + 90, marginTop: 22 }} />
            ))}
          {!loading && shown.length === 0 && (
            <div className="my-auto flex-1 rounded-xl border border-dashed border-border px-4 py-6 text-center text-[12px] text-muted-foreground">
              {needle && alerts.length > 0 ? `Nothing matches “${q.trim()}”.` : 'No open alerts match this filter.'}
            </div>
          )}
          {shown.map((a, i) => {
            const sec = alertSeconds(a);
            const prev = i > 0 ? alertSeconds(shown[i - 1]) : null;
            const gap = sec && prev ? spanLabel(prev - sec) : null;
            const color = severityColor(a.severity);
            return (
              <div key={a.id || `${a.cameraId}-${i}`} className="flex flex-none snap-start flex-col" style={{ width }}>
                {/* The rail: the line, this alert's dot, and how long before the one to its left. */}
                <div className="relative mb-1.5 h-[20px]" aria-hidden>
                  <span className="absolute left-[-12px] right-0 top-1/2 h-px" style={{ backgroundColor: 'var(--border)' }} />
                  <span
                    className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                    style={{
                      left: '50%',
                      width: i === 0 ? 15 : 12,
                      height: i === 0 ? 15 : 12,
                      backgroundColor: color,
                      boxShadow: `0 0 0 3px ${tint(color, 22)}`,
                    }}
                  />
                  {gap && (
                    <span
                      className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full px-1.5 text-[10px] font-semibold tabular-nums text-muted-foreground"
                      style={{ backgroundColor: SUNK, marginLeft: -6 }}
                    >
                      {gap}
                    </span>
                  )}
                </div>
                <TimelineCard
                  alert={a}
                  width={width}
                  newest={i === 0}
                  place={places?.[a.cameraId]}
                  loadPreview={loadPreview}
                  onOpen={onOpenAlert ? () => onOpenAlert(a) : undefined}
                />
              </div>
            );
          })}
          {onLoadMore && shown.length > 0 && !needle && (
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore}
              className={`mt-[26px] flex w-[96px] flex-none items-center justify-center self-stretch rounded-xl border border-dashed border-border text-[12px] font-medium text-muted-foreground hover:text-foreground disabled:opacity-60 ${FOCUS}`}
            >
              {loadingMore ? 'Loading…' : 'Older'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
